import config from '@/config';
import GoogleAnalyticsProvider from './providers/ga-provider';
import MixpanelProvider from './providers/mixpanel-provider';
import { logIt } from '@/services/logger';
import { getDeviceInfo } from './utils/device-info';

class TrackingService {
  constructor() {
    this.providers = [];
    this.isInitialized = false;
    this.userTraits = {};
    this.deviceInfo = {};
  }

  isTestCompany() {
    if (typeof window === "undefined") return false;
    try {
      const profile = window.localStorage.getItem("Profile");
      if (profile) {
        const profileData = JSON.parse(profile);
        return profileData.IsTestCompany === true;
      }
    } catch (e) {}
    return false;
  }

  /**
   * Initialize the tracking service with providers.
   * @param {object} options - Configuration options.
   * @param {string|null} [options.gaTrackingId] - Optional override for GA Tracking ID.
   * @param {string|null} [options.mixpanelToken] - Optional override for Mixpanel Token.
   */
  init(options = {}) {
    if (this.isInitialized) {
      console.warn("TrackingService is already initialized.");
      return;
    }

    if (this.isTestCompany()) {
      this.isInitialized = true;
      return;
    }

    // Capture device info (client-side only)
    if (typeof window !== "undefined") {
      this.deviceInfo = getDeviceInfo();
    }

    // Initialize Google Analytics Provider
    const gaTrackingId = options.gaTrackingId || config.GATrackingId;

    if (gaTrackingId) {
      const gaConfig = {
        trackingId: gaTrackingId,
      };
      const gaProvider = new GoogleAnalyticsProvider(gaConfig);
      gaProvider.init();
      this.providers.push(gaProvider);
    }

    // Initialize Mixpanel Provider
    const mixpanelToken = options.mixpanelToken || config.MixpanelToken;

    if (mixpanelToken) {
      const mixpanelConfig = {
        token: mixpanelToken,
      };
      const mixpanelProvider = new MixpanelProvider(mixpanelConfig);
      mixpanelProvider.init();
      this.providers.push(mixpanelProvider);
    }

    this.isInitialized = true;

    logIt(
      "log",
      `TrackingService initialized with ${this.providers.length} providers.`,
    );
  }

  /**
   * Track an event across all registered providers.
   * @param {string} eventName - Name of the event.
   * @param {object} properties - Event properties.
   */
  track(eventName, properties = {}) {
    if (!this.isInitialized) {
      // console.warn('TrackingService not initialized. Call init() first.');
    }

    if (this.isTestCompany()) {
      return;
    }

    // Add event_datetime (ISO timestamp) and ensure timezone are included
    const eventMetadata = {
      event_datetime: new Date().toISOString(),
      timezone:
        this.userTraits?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Add device info to event
    const deviceMetadata = this.deviceInfo;

    const enhancedProperties = {
      ...this.userTraits,
      ...properties,
      ...eventMetadata,
      ...deviceMetadata,
    };

    this.providers.forEach((provider) => {
      try {
        provider.track(eventName, enhancedProperties);
      } catch (error) {
        logIt("Error", `Error tracking event with ${provider.name}:`, error);
      }
    });
  }

  /**
   * Identify a user across all registered providers.
   * @param {string} userId - User ID.
   * @param {object} traits - User traits.
   */
  identify(userId, traits = {}) {
    this.userTraits = { ...traits, userId };

    if (this.isTestCompany()) {
      return;
    }

    this.providers.forEach((provider) => {
      try {
        provider.identify(userId, traits);
      } catch (error) {
        logIt("Error", `Error identifying user with ${provider.name}:`, error);
      }
    });
  }

  /**
   * Track page view.
   * @param {string} path - Page path.
   * @param {object} properties - Page properties.
   */
  page(path, properties = {}) {
    if (this.isTestCompany()) {
      return;
    }

    // Only send page-specific properties, not user traits
    // User traits are already set via identify() and user_properties
    this.providers.forEach((provider) => {
      try {
        provider.page(path, properties);
      } catch (error) {
        logIt(
          "Error",
          `Error tracking page view with ${provider.name}:`,
          error,
        );
      }
    });
  }
}

// Export a singleton instance
const trackingService = new TrackingService();
export default trackingService;
